/*
 * Quiz purchase adapter. No answers, health concerns, or emails enter this module.
 *
 * PXQuizCommerce.create({ catalog: PX_DATA.products, mode: 'preview', currency: 'USD' })
 *   getProduct(handle) -> catalog product | null
 *   defaults(handle) -> { color, size: '' }; size is always a deliberate choice
 *   resolve(handle, { color, size, id? }) -> exact catalog variant | null
 *   sizeOptions(handle, color) -> [{ size, available }]
 *   imageFor(handle, color) -> matching-color asset path | ''
 *   priceFor(handle, selection?) -> integer minor currency units | null
 *   add(handle, selection, quantity = 1), getCart(), remove(id, expectedVariantId?), clear() -> Promise<Cart>
 * Quantity is an integer from 1 to 99, subject to the variant's quantity rules.
 * Native additions use one POST for the requested quantity; Shopify validates
 * the final cart against current stock and quantity limits.
 * Cart = { items: [{ id, key?, handle, title, color, size, quantity, price,
 *                   lineTotal, image, isQuizProduct }], itemCount, total, currency, mode }
 * Preview totals use the catalog snapshot; they are not live checkout totals.
 * Preview storage contains only variant id, handle, and quantity. It is revalidated
 * against the catalog on every read. A blocked storage API falls back to memory.
 *
 * Native mode must be explicitly configured with mode: 'shopify' and a catalog
 * rendered from current Shopify products. Pass Shopify.routes.root as root.
 * Neither window.Shopify nor a host name enables native mode automatically.
 * Only public shipping timing is added as an order-item property; no health answers.
 * POST requests are never retried. An uncertain response carries uncertain:true
 * and cartUrl: the UI must offer View cart before another attempt.
 * Ajax contract: https://shopify.dev/docs/api/ajax/reference/cart
 * Tests can inject storage and fetch; no real network is required.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.PXQuizCommerce = factory();
}(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const STORAGE_KEY = 'px-quiz-cart-v74';
  const MAX_PREVIEW_QUANTITY = 99;
  const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
  const minorUnits = (value) => Number.isSafeInteger(value) && value >= 0 ? value : null;
  const variantId = (value) => /^\d+$/.test(String(value ?? '')) ? String(value) : '';

  function create(options = {}) {
    const catalog = Array.isArray(options.catalog) ? options.catalog : [];
    const mode = options.mode === undefined ? 'preview' : options.mode;
    if (!['preview', 'shopify'].includes(mode)) throw new TypeError('Unknown commerce mode.');
    const currency = typeof options.currency === 'string' ? options.currency : 'USD';
    const suppliedRoot = options.root || '/';
    if (!/^\/(?:[a-zA-Z0-9_-]+\/)*$/.test(suppliedRoot)) throw new TypeError('Use a same-origin locale root ending in /.');
    const cartUrl = `${suppliedRoot}cart`;
    const products = new Map(catalog.filter((item) => item && typeof item.handle === 'string').map((item) => [item.handle, item]));
    const variants = new Map();
    products.forEach((product) => (product.variants || []).forEach((variant) => {
      if (variantId(variant.id)) variants.set(variantId(variant.id), { product, variant });
    }));
    let storage = null;
    try { storage = own(options, 'storage') ? options.storage : globalThis.sessionStorage; } catch (_) { /* Use memory when storage is unavailable. */ }
    const fetcher = options.fetch || (typeof globalThis.fetch === 'function' ? globalThis.fetch.bind(globalThis) : null);
    let memory = [];
    let busy = false;
    let pendingAddReview = null;

    const error = (code, message, extra = {}) => Object.assign(new Error(message), { code, cartUrl, uncertain: false }, extra);
    const getProduct = (handle) => products.get(handle) || null;
    const variantsFor = (handle) => getProduct(handle)?.variants || [];
    const available = (product, variant) => product?.available !== false && variant?.available === true && minorUnits(variant.price) !== null;

    function defaults(handle) {
      const product = getProduct(handle);
      if (!product) return { color: '', size: '' };
      const colors = product.colors || [];
      const color = colors.find((item) => variantsFor(handle).some((variant) => variant.color === item && available(product, variant)))
        || colors[0] || variantsFor(handle)[0]?.color || '';
      return { color, size: '' };
    }

    function resolve(handle, selection = {}) {
      if (!selection || typeof selection.size !== 'string' || !selection.size || typeof selection.color !== 'string') return null;
      return variantsFor(handle).find((variant) => variant.color === selection.color && variant.size === selection.size
        && (!own(selection, 'id') || variantId(variant.id) === variantId(selection.id))) || null;
    }

    function sizeOptions(handle, color) {
      const product = getProduct(handle);
      const sizes = product?.sizes || [...new Set(variantsFor(handle).map((variant) => variant.size))];
      return sizes.map((size) => ({ size, available: variantsFor(handle).some((variant) => variant.size === size && variant.color === color && available(product, variant)) }));
    }

    function imageFor(handle, color) {
      const images = getProduct(handle)?.images || [];
      const matching = images.filter((image) => image.color === (color || defaults(handle).color));
      // Never show a different color as though it were the selected variant.
      const image = matching.find((item) => item.role === 'angled')
        || matching.find((item) => item.role === 'pair' || item.role?.endsWith('-pair')) || matching[0];
      return typeof image?.path === 'string' ? image.path : '';
    }

    function priceFor(handle, selection = {}) {
      const variant = resolve(handle, selection);
      if (variant) return minorUnits(variant.price);
      if (selection?.size) return null;
      const candidates = variantsFor(handle).filter((item) => (!selection?.color || item.color === selection.color) && minorUnits(item.price) !== null);
      const inStock = candidates.filter((item) => available(getProduct(handle), item));
      const prices = (inStock.length ? inStock : candidates).map((item) => item.price);
      return prices.length ? Math.min(...prices) : null;
    }

    function quantityLimit(variant) {
      const maximum = variant.quantityRule?.max;
      return Number.isSafeInteger(maximum) && maximum > 0 ? Math.min(maximum, MAX_PREVIEW_QUANTITY) : MAX_PREVIEW_QUANTITY;
    }

    function sanitize(raw) {
      if (!Array.isArray(raw)) return [];
      const clean = new Map();
      raw.forEach((item) => {
        if (!item || !Number.isSafeInteger(item.quantity) || item.quantity <= 0) return;
        const record = variants.get(variantId(item.id));
        if (!record || item.handle !== record.product.handle || !available(record.product, record.variant)) return;
        const id = variantId(record.variant.id);
        const quantity = Math.min((clean.get(id)?.quantity || 0) + item.quantity, quantityLimit(record.variant));
        clean.set(id, { id, handle: record.product.handle, quantity });
      });
      return [...clean.values()].map(item => {
        const rule = variants.get(item.id).variant.quantityRule || {};
        const increment = rule.increment || 1;
        return { ...item, quantity: Math.floor(item.quantity / increment) * increment };
      }).filter(item => item.quantity >= (variants.get(item.id).variant.quantityRule?.min || 1));
    }

    function readPreview() {
      if (storage) {
        try {
          const saved = storage.getItem(STORAGE_KEY);
          memory = saved === null ? [] : sanitize(JSON.parse(saved));
        } catch (_) { storage = null; }
      }
      memory = sanitize(memory);
      return memory;
    }

    function writePreview(items) {
      memory = sanitize(items);
      if (storage) {
        try { storage.setItem(STORAGE_KEY, JSON.stringify(memory)); } catch (_) { storage = null; }
      }
    }

    function previewCart() {
      const items = readPreview().map((item) => {
        const { product, variant } = variants.get(item.id);
        return {
          ...item, title: product.title, color: variant.color, size: variant.size,
          price: variant.price, lineTotal: variant.price * item.quantity, image: imageFor(product.handle, variant.color), isQuizProduct: true,
        };
      });
      return { items, itemCount: items.reduce((sum, item) => sum + item.quantity, 0), total: items.reduce((sum, item) => sum + item.lineTotal, 0), currency, mode };
    }

    function nativeCart(data) {
      if (!data || !Array.isArray(data.items) || minorUnits(data.total_price) === null) throw error('INVALID_CART', 'The cart could not be refreshed. Open your cart to check it.');
      const items = data.items.map((item) => {
        const id = variantId(item.variant_id || item.id);
        const known = variants.get(id);
        const option = (name) => item.options_with_values?.find((entry) => entry.name.toLowerCase() === name)?.value || '';
        return {
          id, key: item.key || id, handle: item.handle || known?.product.handle || '',
          title: item.product_title || known?.product.title || item.title || '',
          color: option('color') || option('colour') || known?.variant.color || '', size: option('size') || known?.variant.size || '',
          quantity: item.quantity, price: item.final_price ?? item.price,
          lineTotal: item.final_line_price ?? item.line_price,
          ...(item.properties?.['Expected shipping'] ? { expectedShipping: item.properties['Expected shipping'] } : {}),
          image: item.featured_image?.url || item.image || (known ? imageFor(known.product.handle, known.variant.color) : ''), isQuizProduct: Boolean(known),
        };
      });
      const cartDiscounts = Array.isArray(data.cart_level_discount_applications)
        ? data.cart_level_discount_applications.filter(discount => discount && minorUnits(discount.total_allocated_amount) !== null && discount.total_allocated_amount > 0)
          .map(discount => ({ title: typeof discount.title === 'string' ? discount.title : 'Discount', amount: discount.total_allocated_amount }))
        : [];
      return { items, itemCount: data.item_count ?? items.reduce((sum, item) => sum + item.quantity, 0), total: data.total_price, currency: data.currency || currency, mode,
        ...(minorUnits(data.items_subtotal_price) !== null ? { itemsSubtotal: data.items_subtotal_price } : {}), cartDiscounts };
    }

    async function request(path, body) {
      if (!fetcher) throw error('NETWORK_UNAVAILABLE', 'The cart connection is unavailable.');
      const mutation = body !== undefined;
      let response;
      try {
        response = await fetcher(`${suppliedRoot}${path}`, {
          method: mutation ? 'POST' : 'GET', credentials: 'same-origin',
          headers: { Accept: 'application/json', ...(mutation ? { 'Content-Type': 'application/json' } : {}) },
          ...(mutation ? { body: JSON.stringify(body) } : {}),
        });
      } catch (_) {
        throw error(mutation ? 'NETWORK_UNCERTAIN' : 'NETWORK_ERROR', mutation
          ? 'We could not confirm the update. Open your cart before trying again.'
          : 'The cart could not be loaded. Please try again.', { uncertain: mutation });
      }
      const partialAddPossible = mutation && path === 'cart/add.js' && response.status === 422;
      let data;
      try { data = await response.json(); } catch (_) {
        const definiteClientFailure = response.status >= 400 && response.status < 500 && response.status !== 422;
        const uncertain = mutation && !definiteClientFailure;
        throw error(uncertain ? 'RESPONSE_UNCERTAIN' : mutation ? 'CART_ERROR' : 'INVALID_CART', 'Open your cart to check the latest selection.', { uncertain, status: response.status });
      }
      if (!response.ok) {
        const uncertain = partialAddPossible || (mutation && response.status >= 500);
        const failure = error(response.status === 422 ? 'UNAVAILABLE' : uncertain ? 'RESPONSE_UNCERTAIN' : 'CART_ERROR',
          typeof data?.description === 'string' ? data.description : 'Your cart could not be updated. Check your selection and try again.',
          { status: response.status, uncertain });
        throw failure;
      }
      return data;
    }

    async function getCart(options = {}) {
      const review = options?.review === true ? pendingAddReview : null;
      const cart = mode === 'preview' ? previewCart() : nativeCart(await request('cart.js'));
      if (review && pendingAddReview === review) pendingAddReview = null;
      return cart;
    }

    async function mutate(callback) {
      if (busy) throw error('BUSY', 'Your selection is already being added.');
      busy = true;
      try { return await callback(); } finally { busy = false; }
    }

    async function add(handle, selection, quantity = 1) {
      return mutate(async () => {
        if (pendingAddReview) {
          throw error('CART_REVIEW_REQUIRED', pendingAddReview.message + ' Open your cart to review it before adding again.', { uncertain: true, status: pendingAddReview.status, ...(pendingAddReview.added === true ? { added: true } : {}) });
        }
        const product = getProduct(handle);
        if (!product) throw error('UNKNOWN_PRODUCT', 'Please choose a product again.');
        if (!selection?.size) throw error('SIZE_REQUIRED', 'Choose your size first.');
        const variant = resolve(handle, selection);
        if (!variant || !variantId(variant.id)) throw error('INVALID_VARIANT', 'Choose an available color and size.');
        if (!available(product, variant)) throw error('UNAVAILABLE', 'This color and size are unavailable. Please choose another.');
        if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > MAX_PREVIEW_QUANTITY) throw error('INVALID_QUANTITY', 'Choose a whole-number quantity between 1 and 99.');
        const minimum = variant.quantityRule?.min || 1;
        const increment = variant.quantityRule?.increment || 1;
        if (quantity < minimum || quantity % increment !== 0) throw error('QUANTITY_RULE', `Choose at least ${minimum}, in multiples of ${increment}.`);
        if (quantity > quantityLimit(variant)) throw error('QUANTITY_LIMIT', 'This quantity exceeds the limit for this selection.');
        if (mode === 'preview') {
          const items = readPreview().map((item) => ({ ...item }));
          const id = variantId(variant.id);
          const existing = items.find((item) => item.id === id);
          if ((existing?.quantity || 0) + quantity > quantityLimit(variant)) throw error('QUANTITY_LIMIT', 'This selection has reached the quantity limit.');
          if (existing) existing.quantity += quantity;
          else items.push({ id, handle, quantity });
          writePreview(items);
          return previewCart();
        }
        try {
          const properties = globalThis.PXBackorder?.properties(product.handle);
          if (properties) await globalThis.PXBackorder.prepareExisting(product.handle, variant.id, request);
          const added = await request('cart/add.js', { items: [{ id: variantId(variant.id), quantity, ...(properties ? { properties } : {}) }] });
          if (!Array.isArray(added?.items) || !added.items.some((item) => variantId(item.variant_id || item.id) === variantId(variant.id))) {
            throw error('RESPONSE_UNCERTAIN', 'We could not confirm the update. Open your cart before trying again.', { uncertain: true });
          }
          // The add response contains added lines, not the entire cart. Refresh once.
          try { return await getCart(); } catch (_) {
            throw error('CART_REFRESH_FAILED', 'Your selection was added. Open your cart to see it.', { uncertain: true, added: true });
          }
        } catch (failure) {
          if (failure.uncertain) pendingAddReview = failure;
          throw failure;
        }
      });
    }

    async function remove(id, expectedVariantId) {
      return mutate(async () => {
        if (mode === 'preview') {
          writePreview(readPreview().filter((item) => item.id !== String(id)));
          return previewCart();
        }
        const cart = await getCart();
        const exactKey = cart.items.find((item) => item.key === String(id));
        const matches = exactKey ? [exactKey] : cart.items.filter((item) => item.id === String(id));
        if (matches.length > 1) throw error('AMBIGUOUS_LINE', 'Open your cart to remove this selection.');
        if (!matches.length) {
          const expected = variantId(expectedVariantId);
          if (expected && cart.items.some(item => item.id === expected)) {
            throw error('STALE_LINE', 'Your cart changed. Review the refreshed item and choose Remove again.', { cart });
          }
          return cart;
        }
        return nativeCart(await request('cart/change.js', { id: matches[0].key, quantity: 0 }));
      });
    }

    async function clear() {
      return mutate(async () => {
        if (mode !== 'preview') throw error('LIVE_CLEAR_DISABLED', 'Manage your items in the cart.');
        writePreview([]);
        return previewCart();
      });
    }

    return Object.freeze({ mode, currency, cartUrl, normalizeCart: nativeCart, getProduct, defaults, resolve, sizeOptions, imageFor, priceFor, add, getCart, remove, clear });
  }

  return Object.freeze({ create, STORAGE_KEY });
}));
